import React from "react";
import { Clock, Search, Heart, UserCheck, ListFilter } from "lucide-react";
import MobileSelect from "../../components/MobileSelectDropdown";
import AppInput from "../../components/ui/AppInput";
import { ICONS as GLOBAL_ICONS } from "../../utils/icons";

export default function CounselingSidebarPanel({
  isMobile,
  isTeacher,
  setTargetGroup,
  targetGroup,
  theme,
  searchQuery,
  setSearchQuery,
  filterType,
  setFilterType,
  filteredStudents,
  selectedStudentId,
  visitLogs,
  handleStudentClick,
  sortType,
  setSortType,
  studentMetrics,
}) {
  const accentColor = theme?.primaryHex || "#354F38";

  return (
    <div
      className={`bg-white/70 backdrop-blur-xl rounded-[2.5rem] shadow-[0_8px_30px_rgb(0,0,0,0.03)] border border-white/60 flex flex-col overflow-hidden h-full relative z-10 transition-all duration-500 ${isMobile ? "rounded-none border-x-0 !bg-transparent !shadow-none !border-none" : ""}`}
    >
      <div className={`p-6 pb-4 border-b border-gray-100 ${isMobile ? "!p-2 !pb-0 !bg-none !border-none" : ""}`}>
        <h2 className="hidden md:flex text-2xl font-black text-gray-800 font-do-hyeon mb-5 items-center gap-2 tracking-tight">
          <Heart fill={accentColor} className="text-transparent" size={24} />
          관심 학생 목록
        </h2>

        {!isTeacher && (
          <div className="hidden md:flex bg-gray-100/80 rounded-2xl p-1.5 border border-gray-200/50 shadow-inner h-12 items-center gap-1 mb-5">
            <button
              onClick={() => setTargetGroup("students")}
              className={`flex-1 h-9 rounded-xl text-[13px] font-black transition-all duration-300 flex items-center justify-center ${targetGroup === "students" ? "text-white shadow-md" : "text-gray-400 hover:text-gray-600"}`}
              style={targetGroup === "students" ? { backgroundColor: accentColor } : {}}
            >
              학생
            </button>
            <button
              onClick={() => setTargetGroup("teachers")}
              className={`flex-1 h-9 rounded-xl text-[13px] font-black transition-all duration-300 flex items-center justify-center ${targetGroup === "teachers" ? "text-white shadow-md" : "text-gray-400 hover:text-gray-600"}`}
              style={targetGroup === "teachers" ? { backgroundColor: accentColor } : {}}
            >
              교사
            </button>
          </div>
        )}

        <div className="hidden md:block mb-5">
          <AppInput
            placeholder="이름으로 찾기..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onClear={() => setSearchQuery("")}
            icon={Search}
            className="py-3.5 rounded-2xl bg-white border border-gray-100/60 shadow-sm outline-none transition-all duration-300 font-bold text-gray-700 placeholder-gray-300 text-sm focus:border-gray-300 focus:shadow-md"
          />
        </div>

        <div className="hidden md:flex items-center gap-2 mb-4">
          <div className="flex-1">
            <MobileSelect
              value={sortType}
              options={[
                { value: "name", label: "이름 가나다순" },
                { value: "recent", label: "최근 심방순" },
                { value: "grade", label: "학년/부서순" },
              ]}
              onChange={setSortType}
            />
          </div>
          <div className="p-2.5 rounded-xl bg-gray-50 border border-gray-100 text-gray-400">
            <ListFilter size={16} />
          </div>
        </div>

        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
          {[
            { key: "all", label: "전체" },
            { key: "recent", label: "최근 심방" },
            { key: "long_absent", label: "장기 결석" }
          ].map((type) => {
            const isActive = filterType === type.key;
            return (
              <button
                key={type.key}
                onClick={() => setFilterType(type.key)}
                className={`px-4 py-2 rounded-xl text-[12px] font-bold whitespace-nowrap transition-all duration-300 ${isActive ? "text-white shadow-sm" : "bg-white text-gray-400 hover:text-gray-600 border border-gray-100"}`}
                style={isActive ? { backgroundColor: accentColor } : {}}
              >
                {type.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar p-4 md:p-5 space-y-3">
        {filteredStudents.length > 0 ? (
          filteredStudents.map((student, idx) => {
            const isSelected = student.id === selectedStudentId;
            const metrics = studentMetrics[student.id] || { lastVisitDate: "", visitCount: 0 };
            const lastLogDate = metrics.lastVisitDate;
            const logCount = metrics.visitCount;

            return (
              <div
                key={student.id}
                onClick={() => handleStudentClick(student.id)}
                className={`group p-4 rounded-3xl cursor-pointer transition-all duration-500 border relative animate-in fade-in slide-in-from-left-4 ${isSelected ? `bg-white border-transparent shadow-[0_8px_30px_rgb(0,0,0,0.08)] scale-[1.02] ring-1 ring-gray-100` : "bg-white/50 border-transparent hover:bg-white hover:shadow-md hover:border-gray-100"}`}
                style={{ animationDelay: `${Math.min(idx * 50, 400)}ms` }}
              >
                {isSelected && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1.5 h-10 rounded-r-full" style={{ backgroundColor: accentColor }} />
                )}

                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-sm font-black overflow-hidden shadow-sm transition-all duration-500 ${isSelected ? "scale-110 shadow-lg ring-4 ring-gray-50" : "group-hover:scale-105"}`}>
                    {student.photoUrl ? (
                      <img src={student.photoUrl} className="w-full h-full object-cover" alt="" />
                    ) : (
                      <div className="w-full h-full bg-white flex items-center justify-center overflow-hidden">
                        <img
                          src={targetGroup === "teachers" ? (student.gender === "여" ? GLOBAL_ICONS.teacher_female : GLOBAL_ICONS.teacher_male) : student.gender === "남" ? "/images/boy_3d_light.png" : student.gender === "여" ? "/images/girl_3d_light.png" : "/images/boy_3d_light.png"}
                          className="w-full h-full object-cover transform scale-110"
                          alt="avatar"
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-center mb-1">
                      <span className={`font-black text-[15px] ${isSelected ? "text-gray-900" : "text-gray-700"}`}>{student.name}</span>
                    </div>
                    <div className="flex items-center gap-2 group-hover:gap-3 transition-all">
                      <div className="flex items-center gap-1.5 text-[11px] text-gray-400 font-bold bg-gray-100/50 px-2 py-0.5 rounded-lg border border-gray-100/30">
                        <Clock size={10} />
                        {lastLogDate ? lastLogDate.slice(5) : "기록 없음"}
                      </div>
                      {logCount > 0 && (
                        <span className="text-[10px] font-black text-gray-300">
                          총 {logCount}회
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="h-full flex flex-col items-center justify-center opacity-30 py-20 pointer-events-none">
            <Search size={40} className="mb-4 text-gray-300" />
            <p className="text-sm font-black text-gray-400 uppercase tracking-widest">검색 결과가 없습니다</p>
          </div>
        )}
      </div>
    </div>
  );
}
